/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onMounted,
  ref,
  shallowRef,
  triggerRef,
} from "vue";
import { LemmonGrid, type GridSchema } from "@lemmonhq/uikit";
import { Loading } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { CategoriesSheet } from "../components/CategoriesSheet";
import { ServiceScheduleSheet } from "../components/ServiceScheduleSheet";
import { AddServiceModal } from "../components/AddServiceModal";
import type { Category, Service, ServiceSchedule } from "@lemmonhq/types";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

const countScheduledDays = (days: ServiceSchedule | null | undefined) =>
  Object.values(days ?? {}).filter((ranges) => ranges && ranges.length > 0)
    .length;

export const ServicesSubPage = defineComponent({
  name: "ServicesSubPage",
  setup() {
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const error = ref<string | null>(null);
    const data = shallowRef<Service[]>([]);
    const changedRowIds = ref<Set<number | string>>(new Set());
    const changedCategoryRowIds = ref<Set<number | string>>(new Set());
    const saving = ref(false);
    const showAddService = ref(false);
    const categoryEditorServiceId = ref<number | string | null>(null);
    const scheduleEditorServiceId = ref<number | string | null>(null);
    const allCategories = ref<Category[]>([]);

    const hasChanges = computed(() => changedRowIds.value.size > 0);

    const categoryEditorService = computed(
      () =>
        data.value.find((s) => s.id === categoryEditorServiceId.value) ?? null,
    );
    const scheduleEditorService = computed(
      () =>
        data.value.find((s) => s.id === scheduleEditorServiceId.value) ?? null,
    );

    const updateService = (id: number | string, patch: Partial<Service>) => {
      data.value = data.value.map((s) =>
        s.id === id ? { ...s, ...patch } : s,
      );
      changedRowIds.value = new Set([...changedRowIds.value, id]);
      if ("categories" in patch) {
        changedCategoryRowIds.value = new Set([
          ...changedCategoryRowIds.value,
          id,
        ]);
      }
      triggerRef(data);
    };

    const categoriesColumn = computed(() => ({
      key: "categories",
      header: "Categories",
      type: "readonly" as const,
      width: 280,
      editable: false,
      renderCell: (value: Category[] | null, row: Service) => {
        const categories = value ?? [];
        const visible = categories.slice(0, 2);
        const hiddenCount = categories.length - visible.length;
        return (
          <button
            type="button"
            class="btn btn-xs min-h-7 w-full min-w-0 justify-start gap-1.5 border-base-300 bg-base-100 px-2 shadow-none"
            title="Manage categories"
            onClick={(e) => {
              e.stopPropagation();
              categoryEditorServiceId.value = row.id;
            }}
          >
            {categories.length > 0 ? (
              <div class="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                {visible.map((cat) => (
                  <span
                    key={cat.id}
                    class="badge badge-sm max-w-[96px] shrink-0 truncate px-2.5 bg-base-200 text-base-content/70"
                  >
                    {cat.name}
                  </span>
                ))}
                {hiddenCount > 0 && (
                  <span class="shrink-0 text-xs text-base-content/60">
                    +{hiddenCount}
                  </span>
                )}
              </div>
            ) : (
              <span class="flex items-center gap-1 text-base-content/45">
                <span class="material-symbols-outlined text-sm">add</span>
                <span class="text-xs">Add categories</span>
              </span>
            )}
          </button>
        );
      },
    }));

    const scheduleColumn = computed(() => ({
      key: "days",
      header: "Schedule",
      type: "readonly" as const,
      width: 160,
      editable: false,
      renderCell: (value: ServiceSchedule | null, row: Service) => {
        const count = countScheduledDays(value);
        return (
          <button
            type="button"
            class="btn btn-xs min-h-7 w-full justify-start gap-1.5 border-base-300 bg-base-100 px-2 shadow-none"
            title="Manage schedule"
            onClick={(e) => {
              e.stopPropagation();
              scheduleEditorServiceId.value = row.id;
            }}
          >
            <span class="material-symbols-outlined text-sm">
              {count > 0 ? "event_available" : "event_busy"}
            </span>
            <span class="truncate text-xs">
              {count > 0
                ? `${count} day${count !== 1 ? "s" : ""} set`
                : "Always open"}
            </span>
          </button>
        );
      },
    }));

    const schema = computed(
      (): GridSchema<Service> => ({
        primaryKey: "id",
        columns: [
          {
            key: "id",
            header: "ID",
            type: "readonly",
            width: 72,
            frozen: true,
            cellClass: "font-mono text-xs text-base-content/50",
          },
          {
            key: "name",
            header: "Name",
            type: "text",
            width: 220,
            frozen: true,
            editable: true,
            bulkEditable: true,
            required: true,
            placeholder: "Service name",
            cellClass: "font-semibold !text-accent",
          },
          {
            key: "color",
            header: "Color",
            type: "color",
            width: 130,
            editable: true,
            // Suggestions: colors already used by other services (this
            // hierarchy level) plus colors already used by categories (the
            // parent hierarchy level).
            colorPresets: Array.from(
              new Set(
                [
                  ...data.value.map((s) => s.color),
                  ...allCategories.value.map((c) => c.color),
                ].filter((c): c is string => Boolean(c)),
              ),
            ),
          },
          {
            key: "status",
            header: "Status",
            type: "select",
            width: 130,
            editable: true,
            bulkEditable: true,
            options: [
              { value: "active", label: "Active", icon: "check_circle" },
              { value: "inactive", label: "Inactive", icon: "cancel" },
            ],
          },
          {
            key: "type",
            header: "Type",
            type: "select",
            width: 160,
            editable: true,
            bulkEditable: true,
            options: [
              { value: "restaurant", label: "Restaurant", icon: "restaurant" },
              { value: "bar", label: "Bar", icon: "local_bar" },
              { value: "retail", label: "Retail", icon: "store" },
              {
                value: "room_service",
                label: "Room Service",
                icon: "room_service",
              },
              { value: "accommodation", label: "Accommodation", icon: "hotel" },
              { value: "takeaway", label: "Takeaway", icon: "takeout_dining" },
            ],
          },
          categoriesColumn.value,
          scheduleColumn.value,
          {
            key: "description",
            header: "Description",
            type: "text",
            width: 300,
            editable: true,
            multiline: true,
            placeholder: "Short description",
          },
          {
            key: "visible_only_to_staff",
            header: "Staff Only",
            type: "boolean",
            width: 110,
            editable: true,
            bulkEditable: true,
            formatter: (v) => (v ? "Yes" : "No"),
          },
          {
            key: "hide_unavailable",
            header: "Hide Unavailable",
            type: "boolean",
            width: 150,
            editable: true,
            bulkEditable: true,
            formatter: (v) => (v ? "Yes" : "No"),
          },
        ],
      }),
    );

    const loadServices = async () => {
      const services = await api.getServices();
      data.value = services.map((s) => ({ ...s }));
    };

    onMounted(async () => {
      try {
        await Promise.all([
          loadServices(),
          trpc.categories.list.query().then((cats) => {
            allCategories.value = cats as Category[];
          }),
        ]);
      } catch (e) {
        error.value = "Failed to load services";
      } finally {
        loading.value = false;
      }
    });

    const handleCellChange = (
      rowId: string | number,
      field: string,
      value: any,
    ) => {
      const service = data.value.find((s) => s.id === rowId);
      if (!service) return;
      (service as any)[field] = value;
      changedRowIds.value = new Set([...changedRowIds.value, rowId]);
      triggerRef(data);
    };

    const handleBulkChange = (
      rowIds: Set<string | number>,
      field: string,
      value: any,
    ) => {
      for (const id of rowIds) handleCellChange(id, field, value);
    };

    const handleReorder = (
      draggedId: string | number,
      _parentId: string | number | null,
      newIndex: number,
    ) => {
      const items = data.value.map((s) => ({ ...s }));
      const fromIdx = items.findIndex((s) => s.id === draggedId);
      if (fromIdx === -1) return;
      const [moved] = items.splice(fromIdx, 1);
      items.splice(newIndex, 0, moved);
      items.forEach((s, i) => {
        s.order = i;
        changedRowIds.value = new Set([...changedRowIds.value, s.id]);
      });
      data.value = items;
      triggerRef(data);
    };

    const handleSave = async () => {
      saving.value = true;
      try {
        const changed = Array.from(changedRowIds.value)
          .map((id) => data.value.find((r) => r.id === id))
          .filter(Boolean) as Service[];

        await Promise.all(
          changed.map((service) =>
            trpc.services.update.mutate({
              id: service.id,
              name: service.name,
              status: service.status,
              type: service.type,
              description: service.description,
              color: service.color ?? null,
              days: service.days ?? {},
            }),
          ),
        );

        await Promise.all(
          Array.from(changedCategoryRowIds.value).map((id) => {
            const service = data.value.find((s) => s.id === id);
            if (!service) return Promise.resolve();
            return trpc.services.updateCategories.mutate({
              serviceId: service.id as number,
              categoryIds: (service.categories ?? []).map((c) => c.id),
            });
          }),
        );

        changedRowIds.value = new Set();
        changedCategoryRowIds.value = new Set();
        await loadServices();
        toast.showToast("Services saved", { type: "success" });
      } catch (e) {
        toast.showToast("Failed to save services", { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const handleDiscard = async () => {
      loading.value = true;
      try {
        await loadServices();
        changedRowIds.value = new Set();
        changedCategoryRowIds.value = new Set();
      } finally {
        loading.value = false;
      }
    };

    const handleDeleteRows = async (rowIds: Set<string | number>) => {
      if (rowIds.size === 0) return;
      if (
        !confirm(
          rowIds.size === 1
            ? "Delete this service?"
            : `Delete ${rowIds.size} services?`,
        )
      ) {
        return;
      }
      try {
        await Promise.all(
          Array.from(rowIds).map((id) =>
            trpc.services.delete.mutate({ id: id as number }),
          ),
        );
        await loadServices();
        toast.showToast("Services deleted", { type: "success" });
      } catch (e) {
        toast.showToast("Failed to delete services", { type: "error" });
      }
    };

    const handleAddService = async (name: string) => {
      try {
        await trpc.services.create.mutate({ name });
        showAddService.value = false;
        await loadServices();
        toast.showToast("Service created", { type: "success" });
      } catch (e) {
        toast.showToast("Failed to create service", { type: "error" });
      }
    };

    return () => {
      if (loading.value) {
        return (
          <div class="flex h-full w-full items-center justify-center">
            <Loading />
          </div>
        );
      }

      if (error.value) {
        return (
          <div class="flex h-full w-full items-center justify-center text-error">
            {error.value}
          </div>
        );
      }

      return (
        <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
          {/* Header */}
          <SubpageHeader
            title="Services"
            subtitle="Double-click or press Enter to edit · Tab / arrows to navigate · Drag to reorder"
            v-slots={{
              badges: () => (
                <>
                  <span class="rounded-md border border-base-300 bg-base-100 px-2 py-0.5 text-xs font-medium text-base-content/60">
                    {data.value.length}
                  </span>
                  {hasChanges.value && (
                    <span class="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {changedRowIds.value.size} changed
                    </span>
                  )}
                </>
              ),
              default: () => (
                <>
                  <div class="flex items-center gap-2">
                    <button
                      class="btn btn-neutral btn-sm"
                      onClick={() => (showAddService.value = true)}
                    >
                      <span class="material-symbols-outlined text-base">
                        add
                      </span>
                      Add Service
                    </button>
                    {hasChanges.value && (
                      <>
                        <button
                          class="btn btn-ghost btn-sm text-base-content/70"
                          onClick={handleDiscard}
                        >
                          <span class="material-symbols-outlined text-base">
                            undo
                          </span>
                          Discard
                        </button>
                        <button
                          class="btn btn-primary btn-sm"
                          disabled={saving.value}
                          onClick={handleSave}
                        >
                          <span class="material-symbols-outlined text-base">
                            save
                          </span>
                          {saving.value ? "Saving…" : "Save changes"}
                        </button>
                      </>
                    )}
                  </div>
                </>
              ),
            }}
          />

          {/* Grid */}
          <div class="flex-1 overflow-hidden p-3">
            <LemmonGrid
              data={data.value}
              schema={schema.value}
              changedRowIds={changedRowIds.value}
              sortable={true}
              onCellChange={handleCellChange}
              onBulkChange={handleBulkChange}
              onReorder={handleReorder}
              onDeleteRows={handleDeleteRows}
            />
          </div>

          <AddServiceModal
            isOpen={showAddService.value}
            onClose={() => (showAddService.value = false)}
            onAdd={handleAddService}
          />

          {/* Category management sheet */}
          <CategoriesSheet
            subtitle={categoryEditorService.value?.name ?? ""}
            categories={categoryEditorService.value?.categories ?? []}
            availableCategories={allCategories.value}
            isOpen={Boolean(categoryEditorService.value)}
            onClose={() => (categoryEditorServiceId.value = null)}
            onChange={(categories: Category[]) =>
              updateService(categoryEditorServiceId.value!, { categories })
            }
          />

          {/* Schedule management sheet */}
          <ServiceScheduleSheet
            service={scheduleEditorService.value}
            isOpen={Boolean(scheduleEditorService.value)}
            onClose={() => (scheduleEditorServiceId.value = null)}
            onChange={(days: ServiceSchedule) =>
              updateService(scheduleEditorServiceId.value!, { days })
            }
          />
        </div>
      );
    };
  },
});
